/** Where a record's source link points and what it is called (E4, spec
 * D25 §9.4) — shared by SourceLink, SourceCard and the strip. SA-946: a
 * WhatsApp group source links to its own message in the Inbox thread. */
import { useFeature } from '@/hooks'
import type { Order, RecordSource } from '@/types/commerce'

export interface SourceRef {
  kind: RecordSource['kind']
  conversationId: string | null
  /** SA-946 / SA-1095: on a group or email source, the record's own message in the thread. */
  messageId?: string | null
}

/** The inbox thread when the inbox is on, else the plain conversation. */
export function useThreadHref(conversationId: string | null | undefined): string | null {
  const inbox = useFeature('inbox')
  if (!conversationId) return null
  return inbox ? `/inbox/${conversationId}` : `/conversations/${conversationId}`
}

/** The source's link: the thread, and on a group source the message in it. */
export function useSourceHref(source: SourceRef | null | undefined): string | null {
  const thread = useThreadHref(source && source.kind !== 'none' ? source.conversationId : null)
  const inbox = useFeature('inbox')
  if (!thread) return null
  return inbox && source?.messageId
    ? `${thread}?message=${encodeURIComponent(source.messageId)}`
    : thread
}

export function sourceLabel(kind: SourceRef['kind']): string {
  return kind === 'email' || kind === 'whatsapp-group' ? 'Original enquiry' : 'Original conversation'
}

/** The link-shaped ref of a record's source read. */
export function sourceRefOf(source: RecordSource | null | undefined): SourceRef | null {
  if (!source) return null
  return { kind: source.kind, conversationId: source.conversationId, messageId: source.group?.messageId ?? source.email?.messageId ?? null }
}

/** A register row's source from the order alone (the list runs no source
 * probe): an email-born record's channel is `email` (SA-402's sink stamps
 * it), a group-born one's `whatsapp-group` (SA-945's). */
export function sourceRefFromOrder(order: Pick<Order, 'channel' | 'conversationId'>): SourceRef | null {
  if (!order.conversationId) return null
  const kind: SourceRef['kind'] = order.channel === 'email' ? 'email'
    : order.channel === 'whatsapp-group' ? 'whatsapp-group' : 'conversation'
  return { kind, conversationId: order.conversationId }
}
